import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, CardTitle } from "reactstrap";
import ComplianceScreeningDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/screening/DashboardService";
import ChartError from "src/modules/3rd-party-management/pages/admin/compliance/components/dashboard/ChartError";

const MonthKeys = {
    "1": "Jan",
    "2": "Feb",
    "3": "Mar",
    "4": "Apr",
    "5": "May",
    "6": "Jun",
    "7": "Jul",
    "8": "Aug",
    "9": "Sep",
    "10": "Oct",
    "11": "Nov",
    "12": "Dec"
}

const AIFalsePositives = ({ t, filters }) => {
    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-screening-dashboard",
            "ai-false-positives",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceScreeningDashboardService.getInstance();

            return await service.fetchAIFalsePositives(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        },
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Card>
                <CardBody>
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '20%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }}></div>
                </CardBody>
            </Card>
        );
    }

    const {
        series = []
    } = fetchDataQuery.data || {};

    const categories = [];

    const totalFalsePositivesSeries = {
        name: "Total False Positives",
        data: [],
        color: '#1CBB8C'
    };

    series.forEach(element => {
        const monthKey = parseInt((element.month || '').split('-')?.[1] || '0')?.toString();

        categories.push(
            MonthKeys[monthKey]
        );

        totalFalsePositivesSeries.data.push(element.totalFalsePositives);
    });

    const options = {
        chart: {
            height: 320,
            toolbar: {
                show: false
            },
            type: 'line',
            zoom: {
                enabled: false
            }
        },
        legend: {
            show: true,
            position: 'right'
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'straight'
        },
        grid: {
            row: {
                colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                opacity: 0.5
            }
        },
        xaxis: {
            categories: categories
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            decimalsInFloat: 0,

            labels: {
                formatter: (value) =>
                    Math.round(value)
            }
        }
    }

    return (
        <Card className="h-100">
            <CardBody>
                <div className="mb-4">
                    <CardTitle>
                        {t('AI False Positives')}
                    </CardTitle>
                </div>
                <div className="mb-3">
                    {fetchDataQuery.isError ? (
                        <ChartError
                            title={
                                fetchDataQuery.error?.response?.status === 403 ? 
                                'Error 403' : 
                                null
                            }
                            message={
                                fetchDataQuery.error?.response?.status === 403 ? 
                                'Please upgrade your plan to access this feature' : 
                                null
                            }
                            onRetry={fetchDataQuery.refetch}
                        />
                    ) : (
                        <ReactApexChart
                            options={options}
                            series={[totalFalsePositivesSeries]}
                            type="line"
                            height="280"
                        />
                    )}
                </div>
            </CardBody>
        </Card>
    )
}

export default withNamespaces()(
    AIFalsePositives
);